import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import {
  Button,
  Chip,
  FormField,
  Modal,
  TextInput,
  Toggle,
  ToggleChipButton,
} from "@a2z/ui";
import { ApiError } from "@/lib/api/client";
import { endpoints } from "@/lib/api/endpoints";
import type {
  OnlineOrderAcceptResult,
  OnlineOrderCount,
  OnlineOrderView,
  RestaurantSettings,
} from "@/lib/api/types";
import { useTranslate } from "@/lib/i18n";
import { countedKey } from "@/lib/i18n/plural";
import { channelLabel } from "@/lib/invoice/place";
import {
  PERMISSIONS,
  hasPermission,
  useSession,
} from "@/modules/auth/useSession";
import { DoodleBadge } from "@/shared/doodles/DoodleBadge";
import { useErrorMessage } from "@/shared/errors";
import { invalidateInvoiceMutation } from "@/shared/invalidation";
import { useLocalName } from "@/shared/localName";
import { queryKeys, useApiQuery } from "@/shared/queries";
import { usePrivateApi } from "@/shared/usePrivateApi";
import { usePrivateToast } from "@/shared/usePrivateToast";
import { tableAge } from "../lib/floorTile";
import { deliveryMapUrl, deliveryPinIsApproximate } from "../lib/deliveryMap";
import { OrderedByLine } from "./delivery/OrderedByLine";
import type { InvoiceHandoff } from "@/modules/pos/pages/InvoicePage";

/**
 * THE CUSTOMER'S OWN REQUEST FOR A BAG, ON THE FLOOR (F24, §5.12 — plan C6).
 *
 * A customer who reached the storefront by the restaurant's link sends a
 * TAKEAWAY or DELIVERY order. It is a REQUEST, not a bill: it burns no daily
 * sequence and opens nothing until the cashier taps Accept, which runs the
 * ordinary commands server-side (open → add → fire) with the cashier as the
 * actor and hands the bill back — so from there everything the off-premise
 * strip already does applies. Reject (an optional one-line reason the customer
 * reads) and expiry (the store's `onlineOrderExpiryMinutes`, lazy on read)
 * touch no invoice.
 *
 * Drawn on BOTH floors — the till's (`TableGridPage`: the dark look's rows,
 * and under PAPER the top of the Delivery & takeaway tab, `DeliveryTab`) and
 * the back office's live view (`FloorLiveView`, the same tab since
 * 2026-10-20) — and
 * offered on the rung that opens bills (`RESTAURANT_INVOICE_OPEN`), the same
 * gate as "new takeaway". Store revisions refresh the count and requested
 * cards after committed changes, with one shared query for each.
 *
 * The strip also carries the till's PAUSE switch (`POST
 * /restaurant/online-orders/pause`, the cashier's rung — deliberately not the
 * owner's `PUT settings`): a Friday night is floor work. What already arrived
 * can still be accepted while paused, and after the owner switches ordering
 * off — the strip stays on screen for as long as it has cards.
 */

/** Local age/countdown rendering only; this timer performs no requests. */
export const ONLINE_ORDERS_CLOCK_MS = 10_000;

/** The minute chips on Accept — what "about N minutes" the customer reads. */
const READY_MINUTE_CHIPS = [15, 30, 45] as const;

/** The server's bound on a reject reason (`reject_reason` CHECK 1..140). */
const REJECT_REASON_MAX = 140;

/** The expiry countdown turns amber inside this many minutes. */
const EXPIRY_WARN_MINUTES = 2;

/** `INVOICE_OPEN` — the rung the whole feature sits on. */
export function useOnlineOrdersAccess(): boolean {
  const { data: session } = useSession();
  return hasPermission(session, PERMISSIONS.INVOICE_OPEN);
}

/**
 * The two switches, off the settings read the till already makes
 * (`DrawerStrip`, the off-premise dialog). Configuration revisions propagate
 * a pause flipped on another till. One cache entry for every caller.
 */
export function useOnlineOrderingSettings(enabled: boolean) {
  return useApiQuery<RestaurantSettings>(
    queryKeys.settings,
    endpoints.restaurant.settings,
    {
      enabled,
      retry: false,
    },
  );
}

/** Read existing requests even when ordering is disabled; revisions keep it fresh. */
export function useOnlineOrdersCount(enabled: boolean) {
  return useApiQuery<OnlineOrderCount>(
    queryKeys.onlineOrdersCount,
    endpoints.restaurant.onlineOrdersCount,
    {
      enabled,
      refetchOnWindowFocus: true,
      // A missing route is an older API, not a blip.
      retry: false,
    },
  );
}

/**
 * The strip's REQUESTED cards share the store revision listener. A newer count
 * also catches up a list whose earlier request finished before that count.
 */
export function useOnlineOrders(enabled: boolean): {
  orders: OnlineOrderView[];
  requested: number;
  isLoading: boolean;
} {
  const queryClient = useQueryClient();
  const count = useOnlineOrdersCount(enabled);
  const requested = count.data?.requested ?? 0;
  const list = useApiQuery<OnlineOrderView[]>(
    queryKeys.onlineOrdersRequested,
    endpoints.restaurant.onlineOrders("REQUESTED"),
    {
      enabled,
      refetchOnWindowFocus: true,
      retry: false,
    },
  );
  const listed = list.data?.length ?? 0;
  useEffect(() => {
    if (enabled && requested > listed) {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.onlineOrdersRequested,
      });
    }
  }, [enabled, requested, listed, queryClient]);
  return { orders: list.data ?? [], requested, isLoading: list.isLoading };
}

/**
 * A local clock for the age and the expiry countdown — one
 * tick for the whole strip, not one per card.
 */
function useStripClock(): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(
      () => setNow(Date.now()),
      ONLINE_ORDERS_CLOCK_MS,
    );
    return () => window.clearInterval(id);
  }, []);
  return now;
}

/** The customer's FIRST name — what the chit prints and what the pass calls. */
export function customerFirstName(name: string): string {
  return name.trim().split(/\s+/)[0] ?? "";
}

/**
 * B7 (E-5) — who the card names and the cashier dials: the RECIPIENT when the
 * orderer sent the bag to someone else, else the orderer. The staff view still
 * carries the orderer's full `customerName/customerPhone`, so every contact
 * read on the card goes through here (`recipient ?? orderer`).
 */
export function onlineOrderContact(
  order: Pick<OnlineOrderView, "customerName" | "customerPhone" | "recipient">,
): { name: string; phone: string } {
  return order.recipient
    ? { name: order.recipient.name, phone: order.recipient.phone }
    : { name: order.customerName, phone: order.customerPhone };
}

/** The counted "N waiting" sentence, through `countedKey` like every noun. */
function useWaitingLabel() {
  const t = useTranslate();
  return (count: number) =>
    t(
      countedKey(count, {
        one: (m) => m.pos.onlineOrdersWaitingOne,
        two: (m) => m.pos.onlineOrdersWaitingTwo,
        few: (m) => m.pos.onlineOrdersWaitingFew,
        many: (m) => m.pos.onlineOrdersWaiting,
      }),
      { count },
    );
}

/**
 * THE BADGE IN THE TILL'S HEADER — the count of requests waiting, on every
 * till screen, so a cashier on a bill learns of one without walking back to
 * the floor. Nothing at zero: a badge that always shows is one people learn to
 * stop reading, which is the drawer strip's lesson one slot over.
 */
export function OnlineOrdersBadge({ className = "" }: { className?: string }) {
  const enabled = useOnlineOrdersAccess();
  const count = useOnlineOrdersCount(enabled);
  const waiting = useWaitingLabel();
  const requested = count.data?.requested ?? 0;
  if (!enabled || requested === 0) return null;
  const label = waiting(requested);
  return (
    <Chip
      tone="warning"
      role="status"
      title={label}
      aria-label={label}
      data-testid="online-orders-badge"
      className={`no-print shrink-0 tabular-nums ${className}`}
    >
      {requested}
    </Chip>
  );
}

/**
 * The strip. `paper` draws the PAPER floor's section head (badge, title,
 * count); every other look and the dark till keep the quiet caption the
 * off-premise rows above the floor carry.
 */
export function OnlineOrdersStrip({
  paper,
  className = "",
}: {
  paper: boolean;
  /** The page's gutter, applied only when the strip actually renders. */
  className?: string;
}) {
  const t = useTranslate();
  const enabled = useOnlineOrdersAccess();
  const { orders, requested } = useOnlineOrders(enabled);
  const waiting = useWaitingLabel();
  const now = useStripClock();
  const queryClient = useQueryClient();
  const { apiFetch } = usePrivateApi();
  const toast = usePrivateToast();
  const errorMessage = useErrorMessage();

  const settings = useOnlineOrderingSettings(enabled);
  const [pausing, setPausing] = useState(false);
  const paused = settings.data?.onlineOrderingPaused === true;
  const ordering = settings.data?.onlineOrderingEnabled === true;

  /*
   * Nothing for a session that cannot act, and nothing while the owner has
   * ordering OFF and no request is waiting: a strip that says "no orders"
   * on a store that cannot receive one is noise. With ordering off a card
   * that already arrived is still shown and still acceptable — an owner
   * flipping the switch does not strand a customer who already sent.
   */
  if (!enabled) return null;
  if (!ordering && orders.length === 0) return null;

  const togglePause = async (taking: boolean) => {
    const next = !taking;
    setPausing(true);
    try {
      await apiFetch(endpoints.restaurant.onlineOrdersPause, {
        method: "POST",
        body: { paused: next },
      });
      // Reflect at once; the refetch behind it confirms.
      queryClient.setQueryData<RestaurantSettings>(queryKeys.settings, (old) =>
        old ? { ...old, onlineOrderingPaused: next } : old,
      );
      await queryClient.invalidateQueries({ queryKey: queryKeys.settings });
    } catch (failure) {
      toast.error(errorMessage(failure));
    } finally {
      setPausing(false);
    }
  };

  const info =
    requested > 0 || orders.length > 0
      ? waiting(Math.max(requested, orders.length))
      : t((m) => m.pos.onlineOrdersEmpty);

  return (
    <section
      data-ui="floor-area"
      data-testid="online-orders"
      data-paused={paused ? "" : undefined}
      className={`flex flex-col gap-3 pt-2 ${className}`}
    >
      <div
        data-ui="floor-area-head"
        className="flex flex-wrap items-center gap-2.5 pb-2"
      >
        {paper ? (
          <DoodleBadge id="cart-world" size={30} animate={false} />
        ) : null}
        <h2
          data-ui="floor-area-title"
          className={
            paper
              ? undefined
              : "text-[length:var(--font-size-2xs)] font-extrabold text-[color:var(--text-muted)]"
          }
        >
          {t((m) => m.pos.onlineOrdersTitle)}
        </h2>
        <span
          data-ui="floor-area-count"
          data-testid="online-orders-info"
          className={
            paper
              ? undefined
              : "text-[length:var(--font-size-2xs)] font-bold text-[color:var(--text-muted)]"
          }
        >
          {info}
        </span>
        <span className="flex-1" />
        {/* THE TILL'S PAUSE. A switch, on = taking orders: the sentence beside
            it is the state in words, so colour never carries it alone. */}
        <label
          data-testid="online-orders-pause"
          data-paused={paused ? "" : undefined}
          className={[
            "flex items-center gap-2 text-[length:calc(var(--font-size-2xs)_+_0.5px)] font-bold",
            paused
              ? "text-[color:var(--warning)]"
              : "text-[color:var(--text-secondary)]",
          ].join(" ")}
        >
          <span>
            {paused
              ? t((m) => m.pos.onlineOrdersPaused)
              : t((m) => m.pos.onlineOrdersTaking)}
          </span>
          <Toggle
            checked={!paused}
            disabled={pausing || settings.data === undefined}
            ariaLabel={t((m) => m.pos.onlineOrdersPauseLabel)}
            onChange={(taking) => void togglePause(taking)}
          />
        </label>
      </div>
      {orders.length > 0 ? (
        <div className="grid grid-cols-[repeat(auto-fill,minmax(min(320px,100%),1fr))] gap-3">
          {orders.map((order) => (
            <OnlineOrderCard key={order.id} order={order} now={now} />
          ))}
        </div>
      ) : (
        <p
          data-testid="online-orders-empty"
          className="text-[length:calc(var(--font-size-xs)_+_0.5px)] text-[color:var(--text-muted)]"
        >
          {t((m) => m.pos.onlineOrdersEmpty)}
        </p>
      )}
    </section>
  );
}

/**
 * One request: who, how to reach them, what they want, how long it has
 * waited and how long it has left — then Accept (with the minute chips) and
 * Reject. Each card owns its own chip choice and its own in-flight state, so
 * a slow accept on one never greys the one beside it.
 */
function OnlineOrderCard({
  order,
  now,
}: {
  order: OnlineOrderView;
  now: number;
}) {
  const t = useTranslate();
  const localName = useLocalName();
  const navigate = useNavigate();
  const { locale = "ar" } = useParams();
  const queryClient = useQueryClient();
  const { apiFetch } = usePrivateApi();
  const toast = usePrivateToast();
  const errorMessage = useErrorMessage();
  const [minutes, setMinutes] = useState<number | null>(null);
  const [busy, setBusy] = useState<"accept" | "reject" | null>(null);
  const [rejecting, setRejecting] = useState(false);

  const age = tableAge(order.requestedAt, now);
  // A request that just landed says so, rather than the table tile's «من 0 د»
  // (QA round 2026-10-07, finding 5); the paper register already owns the word.
  const ageLabel =
    age.minutes >= 60
      ? t((m) => m.pos.openForHours, { count: Math.floor(age.minutes / 60) })
      : age.minutes === 0
        ? t((m) => m.paperRegister.justNow)
        : t((m) => m.pos.openForMinutes, { count: age.minutes });

  const remainingMs = new Date(order.expiresAt).getTime() - now;
  const remainingMinutes = Math.ceil(remainingMs / 60_000);
  /*
   * Past its time the card is already EXPIRED in the truth — the next read
   * (ours, or any till's) writes it so and the card leaves. Offering Accept
   * on it would only earn the NOT_REQUESTED refusal, so both controls stand
   * down with the countdown (review 2026-10-07, finding 6).
   */
  const expired = remainingMs <= 0;
  const expiry =
    remainingMs <= 0
      ? { label: t((m) => m.pos.onlineOrderExpired), tone: "late" as const }
      : remainingMs < 60_000
        ? {
            label: t((m) => m.pos.onlineOrderExpiresSoon),
            tone: "late" as const,
          }
        : {
            label: t((m) => m.pos.onlineOrderExpiresIn, {
              minutes: remainingMinutes,
            }),
            tone:
              remainingMinutes <= EXPIRY_WARN_MINUTES
                ? ("warn" as const)
                : ("ok" as const),
          };

  // "pasta ×2 · cola ×1" — the lines as the pass reads them, in the screen's
  // language, built here rather than through a key so no noun has to agree
  // with the figure.
  const summary = order.items
    .map(
      (item) =>
        `${localName({ nameAr: item.nameAr, nameEn: item.nameEn })} ×${item.quantity}`,
    )
    .join(" · ");
  const noteLine = order.note
    ? `${t((m) => m.pos.onlineOrderNoteLabel)}: ${order.note}`
    : null;
  // The rider's point on the map (Phase 7): built from the pin's numbers only.
  const mapUrl = deliveryMapUrl(order.deliveryPin);
  const contact = onlineOrderContact(order);
  // «طلبه» only beside a recipient — the orderer IS the contact otherwise.
  const orderedBy = order.recipient ? (order.orderedBy ?? null) : null;
  // The phone was proven (WhatsApp Confirm / its logged-in owner). Shown,
  // never required: the cashier decides exactly as before.
  const verifiedChip = (
    <Chip tone="success" data-testid={`online-order-${order.id}-verified`}>
      {t((m) => m.pos.onlineOrderVerified)}
    </Chip>
  );

  /**
   * A refusal because the card is no longer REQUESTED — accepted on another
   * till, cancelled by the customer, expired — is not a failure of the tap:
   * the card is stale. Re-read, so it leaves the screen.
   */
  const settle = async (failure: unknown) => {
    toast.error(errorMessage(failure));
    if (
      failure instanceof ApiError &&
      failure.code === "RESTAURANT_ONLINE_ORDER_NOT_REQUESTED"
    ) {
      await queryClient.invalidateQueries({ queryKey: queryKeys.onlineOrders });
    }
  };

  const accept = async () => {
    if (busy) return;
    setBusy("accept");
    try {
      const result = await apiFetch<OnlineOrderAcceptResult>(
        endpoints.restaurant.onlineOrderAccept(order.id),
        {
          method: "POST",
          // The minutes only when a chip was picked: absent means "none
          // promised", which the customer's page reads as no estimate.
          body: minutes === null ? {} : { expectedReadyMinutes: minutes },
        },
      );
      toast.success(
        t((m) => m.pos.onlineOrderAcceptedToast, {
          sequence: result.invoice.dailySequence,
        }),
      );
      // The accepted bill opens in the till — the same screen a tapped
      // takeaway tile opens. The server fired its food, so in a paper
      // workflow that screen prints the chit its own «إرسال» would (D18:
      // the paper starts the food; a live print computer prints it instead).
      navigate(`/${locale}/pos/invoices/${result.invoice.id}`, {
        state: { printFired: true } satisfies InvoiceHandoff,
      });
      await invalidateInvoiceMutation(queryClient, result.invoice.id);
      await queryClient.invalidateQueries({ queryKey: queryKeys.onlineOrders });
    } catch (failure) {
      await settle(failure);
    } finally {
      setBusy(null);
    }
  };

  const reject = async (reason: string) => {
    if (busy) return;
    setBusy("reject");
    try {
      const trimmed = reason.trim().slice(0, REJECT_REASON_MAX);
      await apiFetch(endpoints.restaurant.onlineOrderReject(order.id), {
        method: "POST",
        body: trimmed === "" ? {} : { reason: trimmed },
      });
      setRejecting(false);
      toast.success(t((m) => m.pos.onlineOrderRejectedToast));
      await queryClient.invalidateQueries({ queryKey: queryKeys.onlineOrders });
    } catch (failure) {
      await settle(failure);
    } finally {
      setBusy(null);
    }
  };

  return (
    <article
      data-ui="online-order-card"
      data-testid={`online-order-${order.id}`}
      data-channel={order.channel}
      className="flex flex-col gap-2 rounded-[var(--radius-card)] border border-[color:var(--border-strong)] bg-[color:var(--pos-tile)] p-3 text-[color:var(--text)]"
    >
      <div className="flex flex-wrap items-center gap-2">
        <span
          className="text-[length:calc(var(--font-size-md)_+_1px)] font-extrabold"
          data-testid={`online-order-${order.id}-name`}
        >
          <bdi>{customerFirstName(contact.name)}</bdi>
        </span>
        <Chip tone="info" data-testid={`online-order-${order.id}-channel`}>
          {channelLabel(order.channel, t)}
        </Chip>
        {/* B7 — beside a RECIPIENT the chip moves to the «طلبه» line: it
            was the ORDERER's phone that was proven, not the recipient's. */}
        {order.identityVerified && !orderedBy ? verifiedChip : null}
        <span className="flex-1" />
        <span
          className="whitespace-nowrap text-[length:calc(var(--font-size-2xs)_+_0.5px)] font-semibold text-[color:var(--text-muted)]"
          data-testid={`online-order-${order.id}-age`}
        >
          {ageLabel}
        </span>
        <span
          data-tone={expiry.tone}
          data-testid={`online-order-${order.id}-expiry`}
          className={[
            "whitespace-nowrap rounded-full px-2 py-[2px] text-[length:var(--font-size-2xs)] font-bold",
            expiry.tone === "ok"
              ? "bg-[color:var(--info-100)] text-[color:var(--info)]"
              : expiry.tone === "warn"
                ? "bg-[color:var(--warning-100)] text-[color:var(--warning)]"
                : "bg-[color:var(--danger)] text-[color:var(--accent-contrast)]",
          ].join(" ")}
        >
          {expiry.label}
        </span>
      </div>
      {/* The phone, LTR and isolated so an Arabic name beside it cannot flip
          its digits — the number the cashier dials when it goes wrong. */}
      <bdi
        dir="ltr"
        className="self-start text-[length:calc(var(--font-size-xs)_+_0.5px)] font-bold tabular-nums text-[color:var(--text-secondary)]"
        data-testid={`online-order-${order.id}-phone`}
      >
        {contact.phone}
      </bdi>
      {/* «طلبه» — only on a request sent for someone else. The orderer's
          phone stays masked: the cashier calls the recipient. */}
      {orderedBy ? (
        <OrderedByLine
          orderedBy={orderedBy}
          testId={`online-order-${order.id}-ordered-by`}
          trailing={order.identityVerified ? verifiedChip : null}
        />
      ) : null}
      <p
        className="text-[length:calc(var(--font-size-xs)_+_0.5px)] font-semibold"
        data-testid={`online-order-${order.id}-items`}
      >
        {summary}
      </p>
      {order.channel === "DELIVERY" && order.deliveryAddress ? (
        <p
          className="text-[length:var(--font-size-xs)] text-[color:var(--text-secondary)]"
          data-testid={`online-order-${order.id}-address`}
        >
          <bdi>{order.deliveryAddress}</bdi>
        </p>
      ) : null}
      {order.channel === "DELIVERY" && mapUrl ? (
        <p className="flex flex-wrap items-center gap-2 text-[length:var(--font-size-xs)]">
          <a
            href={mapUrl}
            target="_blank"
            rel="noreferrer noopener"
            className="font-bold underline"
            data-testid={`online-order-${order.id}-map`}
          >
            {t((m) => m.pos.onlineOrderMap)}
          </a>
          {deliveryPinIsApproximate(order.deliveryPin) ? (
            <span
              className="text-[color:var(--warning)]"
              data-testid={`online-order-${order.id}-map-approximate`}
            >
              {/* B7 — the cashier rings the person at the door. */}
              {order.recipient
                ? t((m) => m.pos.onlineOrderMapApproximateRecipient)
                : t((m) => m.pos.onlineOrderMapApproximate)}
            </span>
          ) : null}
        </p>
      ) : null}
      {noteLine ? (
        <p
          className="text-[length:var(--font-size-xs)] text-[color:var(--warning)]"
          data-testid={`online-order-${order.id}-note`}
        >
          <bdi>{noteLine}</bdi>
        </p>
      ) : null}
      <div className="mt-1 flex flex-wrap items-center gap-1.5">
        <span className="text-[length:var(--font-size-2xs)] font-bold text-[color:var(--text-muted)]">
          {t((m) => m.pos.onlineOrderReadyIn)}
        </span>
        {READY_MINUTE_CHIPS.map((chip) => (
          <ToggleChipButton
            key={chip}
            size="xs"
            active={minutes === chip}
            aria-pressed={minutes === chip}
            disabled={busy !== null}
            onClick={() =>
              setMinutes((current) => (current === chip ? null : chip))
            }
            data-testid={`online-order-${order.id}-minutes-${chip}`}
          >
            {t((m) => m.pos.onlineOrderMinutesChip, { minutes: chip })}
          </ToggleChipButton>
        ))}
      </div>
      <div className="flex items-center gap-2">
        <Button
          type="button"
          variant="danger-outline"
          size="touch"
          disabled={busy !== null || expired}
          onClick={() => setRejecting(true)}
          data-testid={`online-order-${order.id}-reject`}
        >
          {t((m) => m.pos.onlineOrderReject)}
        </Button>
        <Button
          type="button"
          variant="primary"
          size="touch"
          className="flex-1 font-extrabold"
          disabled={busy !== null || expired}
          isLoading={busy === "accept"}
          onClick={() => void accept()}
          data-testid={`online-order-${order.id}-accept`}
        >
          {t((m) => m.pos.onlineOrderAccept)}
        </Button>
      </div>
      {rejecting ? (
        <RejectDialog
          isSubmitting={busy === "reject"}
          onCancel={() => setRejecting(false)}
          onConfirm={reject}
        />
      ) : null}
    </article>
  );
}

/** One line the customer reads, optional; then the one tap. */
function RejectDialog({
  isSubmitting,
  onCancel,
  onConfirm,
}: {
  isSubmitting: boolean;
  onCancel: () => void;
  onConfirm: (reason: string) => void | Promise<void>;
}) {
  const t = useTranslate();
  const [reason, setReason] = useState("");
  return (
    <Modal
      isOpen
      onClose={onCancel}
      size="sm"
      title={t((m) => m.pos.onlineOrderRejectTitle)}
      closeLabel={t((m) => m.common.close)}
      data-testid="online-order-reject-dialog"
      footerActions={
        <>
          <Button
            variant="outline"
            size="touch"
            disabled={isSubmitting}
            onClick={onCancel}
            data-testid="online-order-reject-cancel"
          >
            {t((m) => m.common.cancel)}
          </Button>
          <Button
            variant="danger"
            size="touch"
            className="font-extrabold"
            isLoading={isSubmitting}
            onClick={() => void onConfirm(reason)}
            data-testid="online-order-reject-confirm"
          >
            {t((m) => m.pos.onlineOrderRejectConfirm)}
          </Button>
        </>
      }
    >
      <FormField
        name="reason"
        label={t((m) => m.pos.onlineOrderRejectReasonLabel)}
      >
        <TextInput
          name="reason"
          value={reason}
          maxLength={REJECT_REASON_MAX}
          placeholder={t((m) => m.pos.onlineOrderRejectReasonPlaceholder)}
          dir="auto"
          onChange={(event) => setReason(event.target.value)}
          data-testid="online-order-reject-reason"
        />
      </FormField>
    </Modal>
  );
}
